<template>
  <div class="donation-amount-selection">
    <option-label :title="t('donationGuide.payment.donationAmountSelection.title')" />

    <!-- Amount buttons and custom input container -->
    <div class="amount-container mt-4">
      <!-- All preset amount buttons -->
      <v-btn
        v-for="(selectableAmount, index) in sortedSelectableAmounts"
        :id="'donationAmountSelect' + selectableAmount.amount"
        :key="selectableAmount.id"
        variant="outlined"
        size="large"
        :aria-label="
          t('aria.label.selectedAmount', {
            amount: selectableAmount.amount,
            currency: currentCurrency,
          })
        "
        :data-cy="'donation-amount-select-button-' + selectableAmount.amount"
        :class="[
          'donation-amount-select-button',
          !isCustomInputFocused &&
          !customDonationAmount &&
          Number(donationAmount) === Number(selectableAmount.amount)
            ? 'selected'
            : '',
        ]"
        @click="selectDonationAmount(selectableAmount)"
      >
        <span>{{ selectableAmount.amount }} {{ currentCurrency }}</span>
      </v-btn>

      <!-- Custom input -->
      <div
        class="amount-input"
        :class="isCustomInputFocused || !!customDonationAmount ? 'selected' : ''"
      >
        <input
          v-model="customDonationAmount"
          type="number"
          class="amount-input__input"
          :min="min"
          :max="max"
          :placeholder="
            String(t('donationGuide.payment.donationAmountSelection.customDonationAmount.label'))
          "
          @focus="isCustomInputFocused = true"
          @blur="handleCustomInputBlur"
          @wheel.prevent.stop
        />
        <div class="amount-input__label">
          {{ currentCurrency }}
        </div>
      </div>
    </div>
    <v-alert
      v-if="showError"
      variant="outlined"
      density="compact"
      border="start"
      color="deep-orange"
      elevation="0"
      class="donation-amount-selection__alert mt-4"
      data-cy="donation-amount-selection-error"
    >
      <v-icon small class="mr-2" color="deep-orange"> mdi-alert-circle-outline </v-icon>
      <span class="donation-amount-selection__alert-text">{{ errorText }}</span>
    </v-alert>

    <!-- Fee information moved to tooltip in summary section -->
    <!-- <div v-if="!hideFee" class="mt-10">
      <div class="admin-fee-input" data-cy="donation-amount-select-adminfee">
        <div class="admin-fee-label">
          {{ t('donationDetails.form.adminFee.label') }}
        </div>
        <div class="admin-fee-amount ml-2">
          {{ n(adminFee, { style: 'currency', currency: currentCurrency }) }}
        </div>
      </div>
      <span>{{ t('donationDetails.form.adminFee.hint') }}</span>
      <span v-if="feeRegulationUrl">
        {{ t('donationGuide.payment.donationAmountSelection.fee.info') }}
        <a :href="feeRegulationUrl" target="_blank" rel="noopener">
          {{ t('donationGuide.payment.donationAmountSelection.fee.hyperlink') }}
        </a>
      </span>
    </div> -->
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import { SelectableAmount } from '@/interfaces/payment/selectableAmount';
import OptionLabel from '../OptionLabel.vue';
import i18n from '@/plugins/i18n';
import { useLabel } from '@/composables/useLabel';
import store from '@/store';
import matomo from '@/services/matomoService';
import { getCurrencyForLocation } from '@/utils/currency';

export default defineComponent({
  components: { OptionLabel },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    modelValue: {
      type: [String, Number],
      default: undefined,
    },
    selectableAmounts: {
      type: Array as () => Array<SelectableAmount>,
      default: () => [],
    },
    max: {
      type: Number,
      default: null,
    },
    min: {
      type: Number,
      default: null,
    },
    adminFee: {
      type: Number,
      default: null,
    },
    feeRegulationUrl: {
      type: String,
      default: null,
    },
    displayFee: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['input', 'update:modelValue', 'hasError'],
  setup(props, { emit }) {
    // instance not required — kept intentionally removed to satisfy linter
    const { t } = useLabel();
    // number formatting is provided by vue-i18n global n on the instance
    const n = (value: number, options) => i18n.vueI18n.global.n(value, options);

    const customDonationAmount = ref(null);
    const isCustomInputFocused = ref(false);
    const sortedSelectableAmounts = ref(
      (props.selectableAmounts || []).sort((a, b) => a.id - b.id)
    );
    const preselectedAmountIndex = ref(1);
    const initialAmount =
      sortedSelectableAmounts.value.length > 1
        ? sortedSelectableAmounts.value[1].amount
        : sortedSelectableAmounts.value[0]?.amount || '';
    const donationAmount = ref(
      props.modelValue !== undefined && props.modelValue !== 0 && props.modelValue !== ''
        ? props.modelValue
        : props.value !== undefined && props.value !== '' && props.value !== 0
          ? props.value
          : initialAmount
    );
    const hideFee = ref(true);
    const errorText = ref<string | null>(null);

    // Emit initial values
    emit('update:modelValue', donationAmount.value);
    emit('input', donationAmount.value);
    emit('hasError', !!errorText.value);

    const showError = computed(() => !!errorText.value);
    const currentCurrency = computed(() =>
      getCurrencyForLocation(store.state.userLocation ?? 'SWEDEN')
    );

    const validateDonationAmount = (value): string => {
      const donationAmount = parseInt(value);
      const trueDonationAmount = parseFloat(value);
      if (typeof donationAmount !== 'number') {
        return t('validation.numeric') as string;
      } else if (!Number.isInteger(trueDonationAmount)) {
        return t('validation.nonInteger') as string;
      } else if (/^\d+\.\d+$/.test(value)) {
        // if the value is an integer, but of a bad form (e.g. 10.0)
        return t('validation.hasDecimal') as string;
      } else if (!!props.max && props.max < donationAmount) {
        return t('validation.maxValue', { max: props.max }) as string;
      } else if (props.min !== null && donationAmount < props.min) {
        return t('validation.minValue', { min: props.min }) as string;
      }

      return '';
    };

    const selectDonationAmount = (selectedAmount: SelectableAmount) => {
      errorText.value = null;
      donationAmount.value = selectedAmount.amount;
      customDonationAmount.value = null;
      matomo.trackEvent('donations_donation_amount_changed', 'donation_details', 'donations_details_amount_selected');
    };

    const handleCustomInputBlur = () => {
      isCustomInputFocused.value = false;
      trackCustomAmountFocus();
    };

    const trackCustomAmountFocus = () => {
      if (customDonationAmount.value !== null) {
        matomo.trackEvent(
          'donations_donation_custom_input_focused',
          'donation_details',
          'donations_details_custom_input_focused'
        );
      }
    };

    watch(customDonationAmount, (value: number | null) => {
      if (!!value) {
        const validCustomAmountResult: string = validateDonationAmount(value);
        if (validCustomAmountResult.length) {
          errorText.value = validCustomAmountResult;
          emit('hasError', true);
        } else {
          emit('hasError', false);
          errorText.value = null;
          donationAmount.value = value;
        }
      } else {
        // reset to preselected amount if custom amount is cleared
        emit('hasError', false);
        errorText.value = null;
        const index = Math.min(
          preselectedAmountIndex.value,
          sortedSelectableAmounts.value.length - 1
        );
        donationAmount.value = sortedSelectableAmounts.value[index]?.amount || '';
      }
    });

    // keep v-model compatibility: emit legacy 'input' and Vue3 'update:modelValue'
    watch(donationAmount, (value) => {
      emit('input', value);
      emit('update:modelValue', value);
    });

    watch(
      () => props.adminFee,
      () => {
        hideFee.value = !props.adminFee || props.adminFee === 0 || !props.displayFee;
      }
    );

    watch(
      () => props.displayFee,
      () => {
        hideFee.value = !props.adminFee || props.adminFee === 0 || !props.displayFee;
      }
    );

    watch(
      () => props.value,
      (newVal) => {
        // only reflect value updates if a modelValue hasn't been provided (v-model takes precedence)
        if (props.modelValue === undefined) donationAmount.value = newVal;
      }
    );

    // If selectable amounts change (e.g. when locale/currency changes), update list and ensure
    // the default selection remains the second item (index 1) when there is no external value/model
    watch(
      () => props.selectableAmounts,
      (newAmounts) => {
        sortedSelectableAmounts.value = (newAmounts || []).sort(
          (a: SelectableAmount, b: SelectableAmount) => a.id - b.id
        );
        preselectedAmountIndex.value = sortedSelectableAmounts.value.length > 1 ? 1 : 0;

        // Always set to second item
        const defaultAmount = sortedSelectableAmounts.value[preselectedAmountIndex.value]?.amount;
        if (defaultAmount && donationAmount.value !== defaultAmount) {
          donationAmount.value = defaultAmount;
          emit('input', donationAmount.value);
          emit('update:modelValue', donationAmount.value);
        }
      },
      { immediate: true }
    );

    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal !== undefined) donationAmount.value = newVal;
      }
    );

    onMounted(() => {
      hideFee.value = !props.adminFee || props.adminFee === 0 || !props.displayFee;

      // ensure selectable amounts are in order
      sortedSelectableAmounts.value = (props.selectableAmounts || []).sort(
        (a: SelectableAmount, b: SelectableAmount) => {
          return a.id - b.id;
        }
      );

      // Always use the second item (index 1) if available as the default selected amount
      preselectedAmountIndex.value = sortedSelectableAmounts.value.length > 1 ? 1 : 0;

      // set fallback selection when neither modelValue nor value is provided
      if (
        props.modelValue === undefined &&
        (props.value === undefined || props.value === '' || props.value === 0)
      ) {
        const index = Math.min(
          preselectedAmountIndex.value,
          sortedSelectableAmounts.value.length - 1
        );
        const defaultAmount = sortedSelectableAmounts.value[index]?.amount;
        if (defaultAmount) {
          donationAmount.value = defaultAmount;
          // make sure parent receives the selection for v-model
          emit('input', donationAmount.value);
          emit('update:modelValue', donationAmount.value);
        }
      }

      emit('hasError', !!errorText.value);
    });

    return {
      customDonationAmount,
      isCustomInputFocused,
      donationAmount,
      hideFee,
      errorText,
      sortedSelectableAmounts,
      preselectedAmountIndex,
      showError,
      currentCurrency,
      selectDonationAmount,
      handleCustomInputBlur,
      trackCustomAmountFocus,
      validateDonationAmount,
      t,
      n,
    };
  },
});
</script>
<style scoped>
.donation-amount-selection__alert {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: rgba(255, 87, 34, 0.05);
  font-size: 0.875rem;
  line-height: 1.3;
  color: #c62828;
  border-left: 3px solid #ff7043;
}

.donation-amount-selection__alert-text {
  flex: 1;
}

.amount-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.amount-container .amount-input {
  grid-column: span 2;
}

.donation-amount-select-button {
  height: 42px !important;
  padding: 8px 20px !important;
  justify-content: flex-start !important;
  border-radius: 20px !important;
  text-align: left !important;
}

.donation-amount-select-button span {
  color: #6b7280 !important;
  font-size: 14px !important;
  font-weight: 700 !important;
}

.donation-amount-select-button.selected {
  background-color: transparent !important;
}

.donation-amount-select-button.selected span {
  color: #047481 !important;
}

.amount-input {
  position: relative;
  height: 42px !important;
  border-radius: 20px !important;
  display: flex;
  align-items: center;
  width: 100%;
  flex: 1;
}

.amount-input__input {
  width: 100%;
  padding-left: 12px !important;
}

.amount-input__input::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.amount-input__label {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  margin-left: 8px;
}

@media (max-width: 600px) {
  .amount-container {
    grid-template-columns: repeat(2, 1fr);
  }

  .amount-container .amount-input {
    grid-column: span 2;
  }
}
</style>
